import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TypingAnimation } from './TypingAnimation';
import ExploreMars from './ExploreMars';
import { PLACEHOLDER_FELLOWS, LEVEL_LABELS } from '../lib/fellowshipConstants';
import '../styles/hero.css';
import '../styles/interactive-effects.css';

const AAT_START    = new Date('2026-04-30T10:00:00+05:30').getTime();
const FLIP_EASE    = [0.16, 1, 0.3, 1];
const API_BASE     = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const SHOW_DELAY   = 1800;   // genie visible for 1.8s
const HOLD_FELLOWS = 7000;   // fellows visible for 7s

const TYPING_SEGMENTS = [
  { text: 'Revolutionize ', class: 'navy-text' },
  { text: 'Teaching.',      class: 'navy-text' },
];

// Smoke SVG paths
function GenieSmokeLayer() {
  return (
    <svg className="genie-smoke-svg" viewBox="0 0 60 140" preserveAspectRatio="none" aria-hidden="true">
      <path className="genie-smoke-path gsp-1" d="M30 140 C18 110 44 85 26 55 C10 28 36 10 30 0" />
      <path className="genie-smoke-path gsp-2" d="M30 140 C46 108 20 80 38 52 C54 24 26 8 30 0" />
      <path className="genie-smoke-path gsp-3" d="M30 140 C22 105 40 78 28 50 C18 25 38 12 30 0" />
    </svg>
  );
}

function GenieParticles() {
  return (
    <>
      {[0,1,2,3,4,5,6,7].map(n => <div key={n} className={`genie-particle gp-${n}`} />)}
    </>
  );
}

// ── Delay helper ─────────────────────────────────────────────────────────────
const ease = FLIP_EASE;
const anim = (delay, extra = {}) => ({
  initial:    { opacity: 0, ...extra.from },
  animate:    { opacity: 1, ...(extra.to ?? {}), transition: { duration: extra.dur ?? 0.6, ease, delay } },
});

export default function Hero() {
  const { user } = useAuth();
  const [visibleWords, setVisibleWords] = useState([]);
  const [aatStarted,   setAatStarted]   = useState(Date.now() >= AAT_START);
  const [showFellows,  setShowFellows]  = useState(false);
  const [fellowsReady, setFellowsReady] = useState(false);
  const [fellows,      setFellows]      = useState([]);
  const [gazeX,        setGazeX]       = useState(0);

  const staticWords = [
    { text: 'Two',              class: 'navy-text', delay: 3500 },
    { text: 'Minutes.',         class: 'gold-text', delay: 3700 },
    { text: 'Infinite Impact.', class: 'gold-text', delay: 4200 },
  ];

  useEffect(() => {
    staticWords.forEach((w, i) => setTimeout(() => setVisibleWords(p => [...p, i]), w.delay));
    const t = setTimeout(() => {
      document.querySelectorAll('#hero .reveal-text, #hero .reveal-up').forEach(el => el.classList.add('active'));
    }, 100);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!aatStarted) {
      const rem = AAT_START - Date.now();
      if (rem > 0) { const t = setTimeout(() => setAatStarted(true), rem); return () => clearTimeout(t); }
    }
  }, [aatStarted]);

  // Fellows — show once after SHOW_DELAY, stay visible permanently
  useEffect(() => {
    const t = setTimeout(() => setShowFellows(true), SHOW_DELAY);
    return () => clearTimeout(t);
  }, []);

  // Delay wrapper width change until genie exit animation completes (720ms)
  useEffect(() => {
    if (!showFellows) { setFellowsReady(false); return; }
    const t = setTimeout(() => setFellowsReady(true), 720);
    return () => clearTimeout(t);
  }, [showFellows]);

  // Gaze shift
  useEffect(() => {
    const seq = [0, -9, 0, 8, 0]; let idx = 0;
    const iv = setInterval(() => { idx = (idx + 1) % seq.length; setGazeX(seq[idx]); }, 1800);
    return () => clearInterval(iv);
  }, []);

  // Fetch fellows
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/public/fellow-of-the-day`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.fellows?.length) setFellows(data.fellows.slice(0, 2)); })
      .catch(() => {});
  }, []);

  const displayFellows = Array.from({ length: 2 }, (_, i) => fellows[i] ?? PLACEHOLDER_FELLOWS[i]);

  return (
    <section className="hero" id="hero">
      <div className="hero-background">
        <div className="gradient-1" /><div className="gradient-2" />
      </div>

      <div className="container hero-content">

        {/* ── Left ── */}
        <div className="hero-text">
          <h1 className="hero-title">
            <span className="title-block typing-line">
              <TypingAnimation segments={TYPING_SEGMENTS} blinkCursor duration={80} />
            </span>
            <span className="title-block">
              <span className={`animated-word shimmer-text navy-text ${visibleWords.includes(0) ? 'visible' : ''}`}>{staticWords[0].text}</span>
              {' '}
              <span className={`animated-word shimmer-text gold-text ${visibleWords.includes(1) ? 'visible' : ''}`}>{staticWords[1].text}</span>
            </span>
            <span className="title-block">
              <span className={`animated-word shimmer-text gold-text ${visibleWords.includes(2) ? 'visible' : ''}`}>{staticWords[2].text}</span>
            </span>
          </h1>

          <p className="hero-subtitle reveal-text">
            Your AI Teaching Co-Instructor — Pack your lecture <br className="desktop-break" />
            in about two minutes, and let the magic happen.
          </p>

          <div className="startup-recognition reveal-up" style={{ transitionDelay: '0.4s' }}>
            <img src="/Dpiit%20logo.png" alt="DPIIT - Startup India" className="recognition-logo" />
            <div className="recognition-text">
              <span className="recognition-title">Recognized by DPIIT, Startup India</span>
              <span className="recognition-desc">TeachGenie™.ai is officially recognized by the Government of India.</span>
            </div>
          </div>

          <div className="hero-buttons reveal-up" style={{ transitionDelay: '0.6s' }}>
            <Link to={user ? '/generator' : '/signup'} className="btn btn-accent btn-large">
              {user ? 'Launch lesson' : (
                <div className="hero-cta-content">
                  <span className="hero-cta-small">Not a member?</span>
                  <span className="hero-cta-big">Register Now</span>
                </div>
              )}
            </Link>
            <Link to="/learn-more" className="btn btn-outline btn-large">About us</Link>
            <ExploreMars />
          </div>

          <div className="trust-logos">
            <span>Of the teachers</span> • <span>For the teachers</span> • <span>By the teachers</span>
          </div>
        </div>

        {/* ── Right: Genie OR Fellows ── */}
        <div className="hero-visual reveal-right" style={{ display:'flex', justifyContent:'center', alignItems:'center' }}>
          <div className={`hero-card-wrapper${fellowsReady ? ' hero-card-wrapper--fellows' : ''}`}>
            <AnimatePresence mode="wait" initial={false}>

              {/* ── Genie Card ─────────────────────────────────────────────── */}
              {!showFellows && (
                <motion.div
                  key="genie"
                  className="hero-card-flip-inner"
                  exit={{ opacity: 0, scale: 0.82, y: -22, filter: 'blur(18px)', transition: { duration: 0.72, ease } }}
                >
                  <div className="hero-flip-face hero-flip-front">
                    <div className="pop-out-character">
                      <motion.div
                        className="genie-alive-outer"
                        initial={{ opacity: 0, filter: 'blur(18px)', scale: 0.94, y: 16 }}
                        animate={{ opacity: 1, filter: 'blur(0px)', scale: 1, y: 0 }}
                        transition={{ duration: 2.5, ease }}
                      >
                        <div className="genie-aura genie-aura-1" aria-hidden="true" />
                        <div className="genie-aura genie-aura-2" aria-hidden="true" />
                        <GenieSmokeLayer />
                        <GenieParticles />
                        <div className="genie-tilt">
                          <div className="genie-motion">
                            <picture>
                              <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                              <img
                                src="/TechGenieMascot.png"
                                alt="TeachGenie™.ai Mascot"
                                className="hero-mascot-image genie-flicker"
                                onError={e => { e.target.onerror = null; e.target.src = 'https://engageneering.org/AAT-Mascot-MUG.png'; }}
                              />
                            </picture>
                            <div className="genie-gaze genie-gaze-l" style={{ '--gx': `${gazeX}px` }} aria-hidden="true" />
                            <div className="genie-gaze genie-gaze-r" style={{ '--gx': `${gazeX}px` }} aria-hidden="true" />
                          </div>
                        </div>
                        <div className="genie-ground-shadow" aria-hidden="true" />
                      </motion.div>
                    </div>
                    <div className="hero-card-base">
                      <div className="sparkles-container">
                        {Array.from({length:12}).map((_,i)=><div key={i} className="sparkle"/>)}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── Fellows Scene — hero background shows through ─────────── */}
              {showFellows && (
                <motion.div
                  key="fellows"
                  className="hero-fellows-scene"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { duration: 0.38 } }}
                  exit={{ opacity: 0, transition: { duration: 0.32 } }}
                >

                  {/* Eyebrow */}
                  <motion.div
                    className="hfs-eyebrow-row"
                    initial={{ opacity: 0, x: -18 }}
                    animate={{ opacity: 1, x: 0, transition: { duration: 0.55, ease, delay: 0.22 } }}
                  >
                    <span className="hfs-eyebrow-text">✦ FELLOW OF THE WEEK ✦</span>
                  </motion.div>

                  {/* Animated rule */}
                  <motion.div
                    className="hfs-rule"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1, transition: { duration: 0.52, ease, delay: 0.38 } }}
                    style={{ transformOrigin: 'left center' }}
                  />

                  {/* Headline — phrase by phrase blur reveal */}
                  <h3 className="hfs-headline" aria-label="Inspire. Impact. Lead.">
                    {['Inspire.', 'Impact.', 'Lead.'].map((word, i) => (
                      <motion.span
                        key={word}
                        className="hfs-word"
                        initial={{ opacity: 0, y: 26, filter: 'blur(8px)' }}
                        animate={{ opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.58, ease, delay: 0.52 + i * 0.13 } }}
                      >
                        {word}
                      </motion.span>
                    ))}
                  </h3>

                  {/* Sub-headline */}
                  <motion.p
                    className="hfs-sub"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.5, ease, delay: 0.88 } }}
                  >
                    One educator. Every week. Chosen for impact.
                  </motion.p>

                  {/* Portraits */}
                  <div className="hfs-fellows">
                    {displayFellows.map((fellow, i) => (
                      <motion.div
                        key={i}
                        className="hfs-fellow"
                        initial={{ opacity: 0, y: 55, filter: 'blur(12px)' }}
                        animate={{
                          opacity: 1,
                          y:      0,
                          filter: 'blur(0px)',
                          transition: { duration: 0.85, ease, delay: 1.05 + i * 0.22 },
                        }}
                        whileHover={{ scale: 1.04, transition: { duration: 0.25 } }}
                      >
                        <div className="hfs-portrait-wrap">
                          <img
                            src={fellow.image_url?.startsWith('http') ? fellow.image_url : `${API_BASE}${fellow.image_url}`}
                            alt={fellow.name}
                            className="hfs-portrait"
                            onError={e => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentNode.classList.add('hfs-portrait-fallback');
                              e.target.parentNode.setAttribute('data-initials', fellow.name.charAt(0));
                            }}
                          />
                          <div className="hfs-portrait-shine" aria-hidden="true" />
                        </div>
                        <motion.p
                          className="hfs-fellow-name"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0, transition: { duration: 0.45, ease, delay: 1.55 + i * 0.18 } }}
                        >
                          {fellow.name.split(' ').slice(0, 2).join(' ')}
                        </motion.p>
                        <motion.span
                          className="hfs-fellow-level"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1, transition: { duration: 0.4, delay: 1.72 + i * 0.18 } }}
                        >
                          {LEVEL_LABELS[fellow.level] ?? fellow.level}
                        </motion.span>
                      </motion.div>
                    ))}
                  </div>

                  {/* CTA */}
                  <motion.div
                    initial={{ opacity: 0, y: 18, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease, delay: 1.92 } }}
                  >
                    <Link to="/become-a-fellow" className="hfs-cta">
                      Become One of Them →
                    </Link>
                  </motion.div>


                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </div>

      </div>
    </section>
  );
}
